import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import { Renter } from '@/utils/types/renter.interface';
import { ScreeningResult } from '../constants';
import { formatCalendarDateAsUtcNoonTimestamp } from '@/shared/utils/calendar-date.util';

const applyNewFormDefaults = (payload: Renter) => {
  // When the landlord's default coverage already includes baseline SDR
  // (sdr_months > 0), skip the customize override and submit as the
  // default flow. Otherwise the (sdr_months + 1) * rent calculation below
  // would double-count against their existing baseline.
  const sdrMonths = payload.selectedSmbPricing?.sdr_months;
  const skipCustomizeOverride =
    payload.includeDepositCoverage && sdrMonths !== undefined && sdrMonths > 0;

  if (payload.includeDepositCoverage && !skipCustomizeOverride) {
    /**
     * The SMB ON path still submits the monolith's "custom coverage" shape for
     * SDR/LG, but the values now come from DDS pricing instead of the old static
     * month radios.
     */
    payload.rentCoverage = RentCoverageTypes.Customize;
    payload.securityDepositCoverage = SecurityDepositTypes.CustomizeOrRemove;
    payload.customCoverage = true;

    if (payload.selectedSmbPricing) {
      payload.leaseGuaranteeMonthsOverride = Math.max(0, payload.selectedSmbPricing.lg_months - 1);
      payload.securityDepositReplacementAmountOverride =
        (payload.selectedSmbPricing.sdr_months + 1) * (payload.grossMonthlyRent ?? 0);
      return;
    }

    if (payload.leaseGuaranteeMonthsOverride) {
      payload.leaseGuaranteeMonthsOverride = Math.max(
        0,
        Number(payload.leaseGuaranteeMonthsOverride) - 1,
      );
    }
    payload.securityDepositReplacementAmountOverride = payload.grossMonthlyRent ?? 0;
    return;
  }

  /**
   * Default SMB submits no longer need explicit coverage amounts. Screening
   * status alone identifies the selected DDS-backed default option.
   */
  payload.rentCoverage = RentCoverageTypes.CompanyDefault;
  payload.securityDepositCoverage = SecurityDepositTypes.CompanyDefault;
  payload.customCoverage = false;
  delete payload.leaseGuaranteeMonthsOverride;
  delete payload.securityDepositReplacementAmountOverride;
};

const applyLegacyFormDefaults = (payload: Renter) => {
  if (payload.rentCoverage === RentCoverageTypes.CompanyDefault) {
    delete payload.leaseGuaranteeMonthsOverride;
  }
  if (payload.securityDepositCoverage === SecurityDepositTypes.CompanyDefault) {
    delete payload.securityDepositReplacementAmountOverride;
  }
};

export const normalizeInviteLeaseDetailsValues = (
  values: Renter,
  useNewForm: boolean,
  isSmbInvite: boolean,
): Renter => {
  const payload = { ...values };
  if (useNewForm) {
    applyNewFormDefaults(payload);
  } else {
    applyLegacyFormDefaults(payload);
  }
  if (payload.customCoverage || (isSmbInvite && payload.includeDepositCoverage)) {
    payload.screeningStatus = ScreeningResult.Denied;
  }
  return payload;
};

export const buildLegacyInviteLeaseDetailsPayload = (values: Renter): Record<string, unknown> => {
  const payload = { ...values };
  delete payload.selectedSmbPricing;
  delete payload.selectedCoverageRuleId;
  // The client-side rename is local to this app; the legacy endpoint's
  // server-side yup validators still key on the original names. Re-key every
  // diverged field at the wire boundary so the server contract stays intact.
  const {
    address,
    leaseStartDate,
    leaseEndDate,
    unit,
    screeningStatus,
    leaseGuaranteeMonthsOverride,
    securityDepositReplacementAmountOverride,
    ...rest
  } = payload;
  return {
    ...rest,
    leaseStart: formatCalendarDateAsUtcNoonTimestamp(leaseStartDate),
    leaseEnd: formatCalendarDateAsUtcNoonTimestamp(leaseEndDate),
    apartmentNumber: unit,
    approvalStatus: screeningStatus,
    monthsOfCoverage: leaseGuaranteeMonthsOverride,
    securityDepositReplacementAmount: securityDepositReplacementAmountOverride,
    building: { monolith_building_ids: [address.monolith_building_id] },
  };
};
